import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ShareBar } from "@/components/ShareBar";
import { ArrowLeft, ExternalLink } from "lucide-react";

const boldParts = (line) =>
  line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>
  );

const renderReview = (text) =>
  String(text).split("\n").map((raw, i) => {
    const line = raw.trim();
    if (!line) return <div key={i} className="h-3" />;
    if (line.startsWith("### ")) return <h4 key={i} className="font-display text-xl text-[#1C1917] mt-8 mb-2">{boldParts(line.slice(4))}</h4>;
    if (line.startsWith("## ")) return <h3 key={i} className="font-display text-2xl text-[#1C1917] mt-10 mb-3">{boldParts(line.slice(3))}</h3>;
    if (line.startsWith("# ")) return <h2 key={i} className="font-display text-3xl md:text-4xl tracking-tight text-[#1C1917] mt-12 mb-4">{boldParts(line.slice(2))}</h2>;
    if (line.startsWith("- ") || line.startsWith("* ")) return <li key={i} className="ml-6 list-disc font-body text-lg leading-loose text-[#292524]">{boldParts(line.slice(2))}</li>;
    return <p key={i} className="font-body text-lg leading-loose text-[#292524] mb-4">{boldParts(line)}</p>;
  });

export default function RecommendationDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/rec/products/${id}`)
      .then((res) => setProduct(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="relative z-10">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <h1 className="font-display text-5xl text-[#1C1917] italic">Prodotto non trovato</h1>
          <Link to="/le-giraffe-consigliano" className="inline-block mt-8 label-ui text-[#9F583F] link-underline">
            Torna ai consigli
          </Link>
        </div>
        <Footer />
      </div>
    );

  if (!product)
    return (
      <div className="relative z-10">
        <Header />
        <p className="max-w-3xl mx-auto px-6 py-40 font-ui text-sm text-[#57534E]">Caricamento…</p>
      </div>
    );

  const imgs = product.images || [];

  return (
    <div className="relative z-10">
      <Header />

      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-14 pb-10" data-testid="rec-detail-content">
        <Link
          to="/le-giraffe-consigliano"
          className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors mb-10"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Tutti i consigli
        </Link>

        {product.category_name && (
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="label-ui text-[#9F583F] border border-[#E7E5E4] px-3 py-1">
              {product.category_name}{product.subcategory_name ? ` › ${product.subcategory_name}` : ""}
            </span>
          </div>
        )}

        <h1 className="font-display text-4xl md:text-6xl leading-[1] tracking-tight text-[#1C1917] mb-10">
          {product.title}
        </h1>

        {/* Product gallery — full image visible, never cropped */}
        {imgs.length > 0 && (
          <div className="mb-10">
            <div className="w-full bg-white border border-[#E7E5E4] flex items-center justify-center p-4 md:p-8">
              <img
                src={mediaUrl(imgs[active])}
                alt={product.title}
                className="max-w-full max-h-[60vh] w-auto h-auto object-contain"
                data-testid="rec-detail-cover"
              />
            </div>
            {imgs.length > 1 && (
              <div className="flex gap-3 mt-4 flex-wrap">
                {imgs.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    data-testid={`rec-detail-thumb-${i}`}
                    className={`w-20 h-20 shrink-0 overflow-hidden border-2 bg-white flex items-center justify-center p-1 transition-colors ${
                      i === active ? "border-[#9F583F]" : "border-[#E7E5E4] hover:border-[#A8A29E]"
                    }`}
                  >
                    <img src={mediaUrl(im)} alt="" className="max-w-full max-h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Primary CTA */}
        <div className="pb-10 border-b border-[#E7E5E4]">
          <a
            href={product.link}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            data-testid="rec-detail-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#9F583F] hover:bg-[#1C1917] text-white label-ui px-7 py-4 transition-colors"
          >
            Scopri di più <ExternalLink size={15} strokeWidth={1.6} />
          </a>
        </div>

        {/* Review body */}
        <div className="article-body mt-10">
          {product.description ? renderReview(product.description) : (
            <p className="font-body italic text-[#57534E]">Recensione in arrivo.</p>
          )}
        </div>

        {/* Secondary CTA */}
        <div className="mt-12">
          <a
            href={product.link}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#9F583F] hover:bg-[#1C1917] text-white label-ui px-7 py-4 transition-colors"
          >
            Scopri di più <ExternalLink size={15} strokeWidth={1.6} />
          </a>
          <p className="label-ui text-[#A8A29E] mt-4">
            Link affiliato: acquistando tramite esso sostieni il blog senza costi aggiuntivi per te.
          </p>
        </div>

        {/* Share */}
        <div className="mt-16 pt-10 border-t border-[#E7E5E4]">
          <ShareBar title={product.title} orientation="horizontal" />
        </div>
      </article>

      <Footer />
    </div>
  );
}
